import Button from "../../Shared/Button";
import Input from "../../Shared/Input";
import { useFormik } from "formik";
import { useMutation } from "@tanstack/react-query";
import * as Yup from "yup";
import { AxiosError } from "axios";
import { ButtonTypes } from "../../../utils/consts";
import Spinner from "../../Shared/Spinner";
import { toast } from "react-toastify";
import { queryClient } from "../../../main";
import Upload from "../../Shared/Upload";
import { useParams } from "react-router-dom";
import { CreateAdmin } from "../../../api/Admins/AddAdmin";
import { useState } from "react";

const validationSchema = Yup.object({
  firstName: Yup.string().required("Category name is required"),
  lastName: Yup.string().required("Category name is required"),
  email: Yup.string().email("Invalid email").required("Email is required"),
  password: Yup.string().required("Password is required"),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("password")], "Passwords must match")
    .required("Confirm Password is required"),
  img_url: Yup.string().required("Image is required"),
});

const AddAdmin = ({ onSuccess }: { onSuccess?: () => void }) => {
  const { id } = useParams();
  const [isUploading, setIsUploading] = useState(false);

  const { mutate, error, isPending } = useMutation({
    mutationFn: CreateAdmin,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adminsList"] });
      toast.success("Admin Added successfully");
      formik.resetForm();
      onSuccess && onSuccess();
    },
    onError: () => {
      formik.setSubmitting(false);
    },
  });
  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      confirmPassword: "",
      img_url: "",
    },
    validationSchema,
    onSubmit: (values) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { confirmPassword, ...rest } = values;
      mutate(rest);
    },
  });

  const handleimgUrlUpload = (fileName: string) => {
    formik.setFieldValue("img_url", fileName);
  };

  return (
    <form onSubmit={formik.handleSubmit} className="pt-5">
      <div className="flex justify-between mb-6">
        <h1 className="text-burntBlack font-bold text-[32px] mt-5">
          {id ? "Edit Admin" : "Create Admin"}
        </h1>
      </div>
      <div className="p-8 rounded-3xl shadow bg-white border border-black/15 min-h-[70vh] flex gap-16">
        <div className="w-full">
          <h1 className="mb-2.5 font-bold text-2xl">Create Admin</h1>
          <p className="text-lightGray font-medium">
            Create a new admin to manage the platform.
          </p>
          <div className="space-y-7 pt-8">
            <Input
              value={formik.values.firstName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.firstName && formik.errors.firstName
                  ? formik.errors.firstName
                  : ""
              }
              className="text-lg font-semibold"
              inputStyles="bg-[#F5F6F8] !h-12 border-black/15 !rounded"
              title="First Name"
              placeholder=""
              name="firstName"
              type="text"
            />
            <Input
              value={formik.values.lastName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.lastName && formik.errors.lastName
                  ? formik.errors.lastName
                  : ""
              }
              className="text-lg font-semibold"
              inputStyles="bg-[#F5F6F8] !h-12 border-black/15 !rounded"
              title="Last Name"
              placeholder=""
              name="lastName"
              type="text"
            />
            <Input
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.email && formik.errors.email
                  ? formik.errors.email
                  : ""
              }
              className="text-lg font-semibold"
              inputStyles="bg-[#F5F6F8] !h-12 border-black/15 !rounded"
              title="Email"
              placeholder=""
              name="email"
              type="text"
            />
            <Input
              value={formik.values.password}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.password && formik.errors.password
                  ? formik.errors.password
                  : ""
              }
              className="text-lg font-semibold"
              inputStyles="bg-[#F5F6F8] !h-12 border-black/15 !rounded"
              title="Password"
              placeholder="**********"
              name="password"
              type="password"
            />
            <Input
              value={formik.values.confirmPassword}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.confirmPassword && formik.errors.confirmPassword
                  ? formik.errors.confirmPassword
                  : ""
              }
              className="text-lg font-semibold"
              inputStyles="bg-[#F5F6F8] !h-12 border-black/15 !rounded"
              title="Confirm Password"
              placeholder="**********"
              name="confirmPassword"
              type="password"
            />
            <Upload
              folder="admins_images"
              onUploadStarts={() => setIsUploading(true)}
              onUploadEnds={() => setIsUploading(false)}
              onFileUploaded={handleimgUrlUpload}
            >
              <Input
                upload={true}
                className="text-lg font-semibold"
                inputStyles="bg-[#F5F6F8] !h-12 border-black/15 !rounded"
                placeholder=""
                title="Admin Image"
                name="img_url"
                type="text"
                value={formik.values.img_url}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.img_url && formik.errors.img_url
                    ? formik.errors.img_url
                    : ""
                }
              />
            </Upload>

            {error instanceof AxiosError && (
              <span className="text-red-500 font-bold text-lg">
                {error?.response?.data?.message || "An error occurred"}
              </span>
            )}
            <Button
              className={`!h-14 w-full !text-lg font-bold rounded-md ${
                isUploading
                  ? "opacity-50 cursor-not-allowed !bg-[#bfbfbf]"
                  : "bg-[#4379EE] hover:!bg-primary"
              }`}
              variant="primary"
              type={ButtonTypes.SUBMIT}
              disabled={formik.isSubmitting || isPending || isUploading}
            >
              {isPending || isUploading ? (
                <>
                  <Spinner /> Loading
                </>
              ) : (
                "Create Admin"
              )}
            </Button>
          </div>
        </div>
      </div>
    </form>
  );
};

export default AddAdmin;
